import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import { Form } from "@/components/ui/form";
import { z } from "zod";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import FormInputField from "@/components/lib/from-input-field";
import { toast } from "react-hot-toast";
import { setItemVariants } from "@/redux/slice/essential-menu-slice/variants-slice";
import { getFirstErrorMessage } from "@/lib/utils";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";

import useAuth from "@/hooks/useAuth";
import { fetchAllFlatMenu } from "@/redux/slice/menu-manage-slice/menu-slice";

export default function CreateItem({
  isOpen,
  onClose,
  title,
  onSuccess,
  quantity = true,
}) {
  const form = useForm({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      flatItem: "",
      item_id: null,
      item_type: "",
      variant_id: null,
      file_manager_id: null,
      file_manager_url: "",
      item_name: "",
      quantity: 1,
      price: null,
      in_price: null,
      out_price: null,
      variant_name: "",
    },
  });
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { flatMenus } = useSelector((state) => state.menuManage.menu);
  const [selectedItem, setSelectedItem] = useState(null);
  const selectedValue = form.watch("flatItem");
  const variantId = form.watch("variant_id");
  const { itemVariants } = useSelector((state) => state.essentialMenu.variants);
  const errorMessage = getFirstErrorMessage(form);

  useEffect(() => {
    if (isOpen && user?.restaurant_id && (!flatMenus || flatMenus.length === 0)) {
      dispatch(fetchAllFlatMenu(user.restaurant_id));
    }
  }, [isOpen, user?.restaurant_id, flatMenus?.length, dispatch]);

  useEffect(() => {
    if (selectedValue && typeof selectedValue === "string") {
      const [id, type] = selectedValue.split("|");
      form.setValue("item_id", Number(id));
      form.setValue("item_type", type);
      const foundItem = flatMenus.find(
        (menu) =>
          String(menu.id) === String(id) &&
          String(menu.menu_type) === String(type)
      );
      setSelectedItem(foundItem || null);
      if (foundItem?.has_variants) {
        const varId = foundItem.variants[0].id;
        form.setValue("variant_id", varId);
      }
    } else {
      setSelectedItem(null);
    }
  }, [selectedValue, flatMenus]);

  useEffect(() => {
    if (variantId && selectedItem?.has_variants) {
      const foundVariant = selectedItem?.variants?.find(
        (item) => String(item.id) === String(variantId)
      );
      form.setValue(
        "price",
        selectedItem?.price ||
          foundVariant?.in_price ||
          null
      );
      form.setValue(
        "in_price",
          foundVariant?.in_price ||
          selectedItem?.price ||
          null
      );
      form.setValue(
        "out_price",
          foundVariant?.out_price ||
          selectedItem?.price ||
          null
      );
      form.setValue(
        "variant_name",
        foundVariant?.name || null
      );
    } else {
      form.setValue(
        "price",
        selectedItem?.price || null
      );
      form.setValue(
        "in_price",
        selectedItem?.price || selectedItem?.in_price
      );
      form.setValue(
        "out_price",
        selectedItem?.price || selectedItem?.out_price
      );
      form.setValue(
        "variant_name",
        ""
      );
    }
  }, [variantId, selectedValue, selectedItem]);

  useEffect(() => {
    if (selectedItem) {
      form.setValue("item_id", selectedItem.id);
      form.setValue("item_name", selectedItem.name);
      form.setValue("file_manager_id", selectedItem.file_manager_id || null);
      form.setValue("file_manager_url", selectedItem.file_manager_url || "");
    }
  }, [selectedItem]);

  const onSubmit = async (data) => {
    try {
      const newItem = {
        ...data,
        unique_id: uuidv4(),
      };

      dispatch(setItemVariants([...itemVariants, newItem]));
      if (onSuccess) {
        onSuccess(newItem);
      }
      toast.success("Item added successfully!");
      form.reset();
      onClose();
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong.");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <div className="py-5 flex flex-col">
          <DialogTitle className="text-lg absolute top-4 font-medium">
            {title || "Create Combo Item"}
          </DialogTitle>
          <Separator className="mt-4" />

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-6 mt-5"
            >
              <div>
                <FormInputField
                  name="flatItem"
                  style="bg-cblack3"
                  placeholder="Select Item name"
                  form={form}
                  selectOption={
                    flatMenus && flatMenus.length > 0
                      ? flatMenus.map((item) => ({
                          label: item.name,
                          value: `${item.id}|${item.menu_type}`,
                        }))
                      : [
                          {
                            label: "No option available",
                            value: "",
                            disabled: true,
                          },
                        ]
                  }
                  label="Select Item"
                  search={true}
                  type="select"
                />
                {errorMessage && (
                  <div className="text-cred text-sm ">{errorMessage}</div>
                )}
              </div>
              {selectedItem && selectedItem.has_variants === true && (
                <FormInputField
                  name="variant_id"
                  style="bg-cblack3"
                  placeholder="Select variant"
                  form={form}
                  selectOption={
                    selectedItem?.variants && selectedItem.variants.length > 0
                      ? selectedItem.variants.map((item) => ({
                          label: item.name,
                          value: item.id,
                        }))
                      : [
                          {
                            label: "No option available",
                            value: "",
                            disabled: true,
                          },
                        ]
                  }
                  label="Select Variant"
                  type="select"
                />
              )}
              {quantity && (
                <FormInputField
                  name="quantity"
                  style="bg-cblack3"
                  placeholder="Quantity"
                  form={form}
                  label="Quantity"
                  type="text"
                />
              )}

              {selectedItem && selectedItem.file_manager_url !== null && (
                <div>
                  <Label>Item Preview</Label>
                  <div className="h-48 w-48 mt-4 overflow-hidden mx-auto">
                    <img
                      className="h-full w-full object-cover"
                      src={selectedItem.file_manager_url}
                      alt={selectedItem.name}
                    />
                  </div>
                </div>
              )}

              <Button type="submit" className="bg-cyellow w-full text-cblack">
                Confirm
              </Button>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const schema = z.object({
  item_id: z.preprocess(
    (val) => (val ? Number(val) : undefined),
    z
      .number({ invalid_type_error: "Item is required" })
      .min(1, "Item is required")
  ),

  item_type: z.string().optional(),
  item_name: z.string().optional(),
  file_manager_url: z.string().optional(),
  variant_id: z.number().nullable().optional(),
  file_manager_id: z.number().nullable().optional(),
  quantity: z.preprocess(
    (val) => {
      if (val === null || val === undefined || val === "") return undefined;
      return Number(val);
    },
    z
      .number({
        invalid_type_error: "Quantity must be a number",
      })
      .min(1, "Quantity must be at least 1")
  ),

  price: z.preprocess(
    (val) =>
      val === null || val === undefined || val === "" ? undefined : Number(val),
    z.number().min(1, "Price must be greater than 0").optional()
  ),
  in_price: z.preprocess(
    (val) =>
      val === null || val === undefined || val === "" ? undefined : Number(val),
    z.number().min(1, "In price must be greater than 0").optional()
  ),
  out_price: z.preprocess(
    (val) =>
      val === null || val === undefined || val === "" ? undefined : Number(val),
    z.number().min(1, "Out price must be greater than 0").optional()
  ),
  variant_name: z.string().optional(),
});
